import { ReactNode, useCallback } from "react";
import { DefaultButton, TextField } from "@fluentui/react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "../../components/Card"
import { CardContent } from "../../components/CardContent";
import { CardHeader } from "../../components/CardHeader"
import { Instructions } from "../../components/Instructions";
import { PageContainer } from "../../components/PageContainer";
import { PageHeader } from "../../components/PageHeader";
import { AppRoutes } from "../../constants/AppRoutes";
import { FormStatus, formStatusToString } from "../../data/models/Form";
import { TaskOutcome } from "../../data/models/Task";
import { GetForm } from "../../data/queries/FormQueries";
import { GetTasks } from "../../data/queries/TaskQueries";
import { QueryKeys } from "../../data/QueryKeys";
import { VPICPreApprovalForm } from "./VPICPreApprovalForm"
import { DetailsCardShimmer, FormCardShimmer } from "../../helpers/Shimmer";
import { TaskHistoryList } from "../../components/TaskHistoryList";
import { ClarificationStage, ComplianceApprovalStage, DealerApprovalStage, FinanceApprovalStage, NoActionsStage, submitForm } from "../../forms/DefaultStages";
import { FormVpicPreApproval } from "../../data/models/FormVpicPreApproval";
import { TaskVpicPreApproval } from "../../data/models/TaskVpicPreApproval";
import { useUserContext } from "../../hooks/useUserContext";
import { hasPermission, hasRole, UserRole } from "../../data/models/User";
import { currencyToString } from "../../utilities/FormUtils";
import { VPIDisplayTextBox } from "../../fields/VPIDisplayTextBox";
import { VPICPreApprovalDetails } from "./VPICPreApprovalDetails";

const FORM_TYPE = 'vpic-preapproval';
const INSTRUCTIONS_URL = '/instructions/VPIC - Pre-Approval Instructions.pdf';

export const VPICPreApprovalWorkflow = (props: any) => 
{
    const params = useParams();
    const nav = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useUserContext();
    const formId = params.formId ?? 'new'; // formId is set to the ID or 'new' the user is creating a new form
    const isNewForm = formId === 'new';

    const formQuery = useQuery(QueryKeys.VPPWPreApproval.Form(formId), async () => GetForm<FormVpicPreApproval>(FORM_TYPE, formId), { enabled: !isNewForm });
    const taskQuery = useQuery(QueryKeys.VPPWPreApproval.Tasks(formId), async () => GetTasks<TaskVpicPreApproval>(FORM_TYPE, formId), { enabled: !isNewForm });

    let detailsView: ReactNode = <DetailsCardShimmer />;
    let formView: ReactNode = <FormCardShimmer />;

    const refreshPage = useCallback(() => { 
        queryClient.invalidateQueries(QueryKeys.VPPWPreApproval.Form(formId)); 
        nav(AppRoutes.Dashboard()); 
    }, [queryClient, formId]);

    const onSaveForm = useCallback(async (formData: Partial<FormVpicPreApproval>, action: 'save-draft' | 'submit') => {
        try
        {
            await submitForm(FORM_TYPE, formQuery.data?.id, formData, action);
            refreshPage();
        }
        catch(error: any)
        {
            console.log(error);
            alert(JSON.stringify(error.response.data));
        }
    }, [formQuery.data, refreshPage]);

    if(isNewForm)
    {
        detailsView = <Instructions url={INSTRUCTIONS_URL} />;
        formView = (
            <>
                <CardHeader>VPIC - Pre-Approval Request</CardHeader>
                <CardContent>
                    <VPICPreApprovalForm onSave={onSaveForm} />
                </CardContent>
            </>
        );
    }
    // If a form item has already been created and is fetched (i.e. not new)
    else if(
        !formQuery.isFetching && formQuery.data &&
        !taskQuery.isFetching && taskQuery.data
    )
    {
        const form = formQuery.data;
        const currentTask = taskQuery.data.find(t => !t.outcome || t.outcome === TaskOutcome.InProgress);

        switch(form.formStatus)
        {
            case FormStatus.Draft:
            case FormStatus.AdvisorRevision:
                detailsView = <Instructions url={INSTRUCTIONS_URL} />;
                break;
            case FormStatus.AdvisorClarification:
            case FormStatus.ComplianceApproval:
            case FormStatus.ComplianceClarification:
            case FormStatus.FinanceApproval:
                detailsView = <VPICPreApprovalDetails form={form} variant="2column" />
                break;
            default:
                detailsView = null;
                break;
        }

        // Set the form view based on form status and user role
        if((form.formStatus === FormStatus.Draft || form.formStatus === FormStatus.AdvisorRevision) && hasPermission(user, 'vpic.write'))
            formView = (
                <>
                    <CardHeader>VPIC - Pre-Approval Request</CardHeader>
                    <CardContent>
                        <VPICPreApprovalForm form={form} onSave={onSaveForm} allowComments={!!currentTask} />
                    </CardContent>
                </>
            );
        else if(form.formStatus === FormStatus.AdvisorClarification && hasPermission(user, 'vpic.write'))
            formView = <ClarificationStage form={form} task={currentTask} onSave={refreshPage} title="Advisor Clarification Requested" />;
        else if(form.formStatus === FormStatus.ComplianceClarification && hasRole(user, UserRole.Compliance))
            formView = <ClarificationStage form={form} task={currentTask} onSave={refreshPage} title="Compliance Clarification Requested" />;
        else if(form.formStatus === FormStatus.ComplianceApproval && hasRole(user, UserRole.Compliance))
            formView = (
                <ComplianceApprovalStage title="Certification" form={form} task={currentTask} onSave={refreshPage} allowDealerClarification={false}>
                    <CardHeader>Attendance Information</CardHeader>
                    <CardContent style={{ display: 'flex', gap: '15px' }}>
                        <VPIDisplayTextBox label="Total Attendance (Estimate)" value={form.attendanceClients + form.attendanceEmployees} />
                        <VPIDisplayTextBox label="Total Cost (Estimate)" value={currencyToString(form.expenseEstimatedTotalCost)} />
                        <VPIDisplayTextBox label="Cost Per Person (Estimate)" value={
                            (form.attendanceClients + form.attendanceEmployees) > 0 ? currencyToString(form.expenseEstimatedTotalCost / (form.attendanceClients + form.attendanceEmployees)) : 'N/A'
                        } />
                    </CardContent>
                </ComplianceApprovalStage>
            );
        else if(form.formStatus === FormStatus.FinanceApproval && hasRole(user, UserRole.Finance))
            formView = <FinanceApprovalStage form={form} task={currentTask} onSave={refreshPage} allowDealerClarification={false} />
        else if(form.formStatus === FormStatus.Completed
            || form.formStatus === FormStatus.Withdrawn 
            || form.formStatus === FormStatus.Rejected)
        {
            formView = (
                <>
                    <CardHeader>{form.formId} | VPIC - Pre-Approval Request</CardHeader>
                    <CardContent>
                        <VPICPreApprovalDetails form={form} />
                    </CardContent>
                </>
            );
        }
        else 
        {
            formView = <NoActionsStage title="VPIC Pre-Approval Request" />;
        }
    }

    return (
        <PageContainer type={detailsView ? '3column' : '2column'}>
            <PageHeader>
                <DefaultButton text="Back" onClick={() => nav(AppRoutes.Dashboard())} />
            </PageHeader>
            { detailsView &&
                <Card style={{ alignSelf: 'flex-start' }}>
                    <CardHeader>{formQuery.data?.formId || 'Details'}</CardHeader>
                    <CardContent>{detailsView}</CardContent>
                </Card>
            }
            <Card>{formView}</Card>
            <Card style={{ alignSelf: 'flex-start' }}>
                <CardHeader>History</CardHeader>
                <TaskHistoryList tasks={taskQuery.data?.filter(t => t.outcome && t.outcome !== TaskOutcome.InProgress)} isLoading={taskQuery.isFetching} />
            </Card>
        </PageContainer>
    );
};
